import {apiClient, FetcherSettings} from './apiClient';
import {dynamicRoute} from './utils';
import {Guest} from '../utils';
import {
  CreateGuestByParticipantPayload,
  CreateGuestByParticipantResponse,
  GetGuestSchemaPayload,
  GuestSchemaResponse,
  LivenessDetection,
} from '../services';

type CreateGuestWithMinDataPayload = Pick<Guest, 'name' | 'surname'> & {
  reservation_id?: string;
  residence_country?: string;
  second_surname?: string;
  birth_date?: string;
  gender?: string;
  nationality?: string;
  citizenship?: string;
  email?: string;
  phone?: string;
  is_leader?: boolean;
  participant_id?: string;
};

class GuestApi {
  ENDPOINTS = {
    oneV4: dynamicRoute({
      regular: (id: string, params = '') => `api/v4/guestapp/guests/${id}/${params}`,
      sdk: (id: string, params = '') => `sdk/api/v1/guestapp/guests/${id}/${params}`,
    }),
    getSchema: dynamicRoute({
      regular: (version = '') => `api/v4/guestapp/guests/schema/${version}`,
      sdk: (version = '') => `sdk/api/v1/guestapp/guests/schema/${version}`,
    }),
    delete: dynamicRoute({
      regular: (id: string) => `api/v3/guests/${id}/`,
      sdk: (id: string) => `sdk/api/v1/guests/${id}/`,
    }),
    create: dynamicRoute({
      regular: (params = '') => `api/v4/guestapp/guests/${params}`,
      sdk: (params = '') => `sdk/api/v1/guestapp/guests/${params}`,
    }),
    patch: dynamicRoute({
      regular: (id: string) => `api/v4/guestapp/guests/${id}/`,
      sdk: (id: string) => `sdk/api/v1/guestapp/guests/${id}/`,
    }),
    livenessDetection: () => 'api/v3/guest-liveness-detection',
    createGuestByParticipant: dynamicRoute({
      regular: () => `api/v4/guestapp/guests/create-from-participant/`,
      sdk: () => `sdk/api/v1/guestapp/guests/create-from-participant/`,
    }),
  };

  async getOne(id: string, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.get<Guest>(this.ENDPOINTS.oneV4(id), {
      requireEncryption: true,
      ...fetcherSettings,
    });
    return response.body;
  }

  async create(payload: FormData, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<Guest>(this.ENDPOINTS.create(), {
      body: payload,
      headers: {},
      ...fetcherSettings,
    });
    return response.body;
  }

  async createGuestByParticipant(
    payload: CreateGuestByParticipantPayload,
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.post<CreateGuestByParticipantResponse>(
      this.ENDPOINTS.createGuestByParticipant(),
      {
        json: payload,
        ...fetcherSettings,
      },
    );
    return response.body;
  }

  async createMin(
    payload: CreateGuestWithMinDataPayload,
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.post<Guest>(this.ENDPOINTS.create('?min=true'), {
      json: payload,
      ...fetcherSettings,
    });

    return response.body;
  }

  async patch(id: string, payload: FormData, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.put<Guest>(this.ENDPOINTS.patch(id), {
      body: payload,
      headers: {},
      ...fetcherSettings,
    });
    return response.body;
  }

  async delete(id: string, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.delete(this.ENDPOINTS.delete(id), fetcherSettings);
    return response.body;
  }

  async getSchema(
    payload: GetGuestSchemaPayload,
    version = '',
    fetcherSettings?: FetcherSettings,
  ) {
    const response = await apiClient.post<GuestSchemaResponse>(
      this.ENDPOINTS.getSchema(version === 'V1' ? '' : version.toLocaleLowerCase()),
      {json: payload, ...fetcherSettings},
    );
    return response.body;
  }

  async livenessDetection(payload: LivenessDetection, fetcherSettings?: FetcherSettings) {
    const response = await apiClient.post<LivenessDetection>(
      this.ENDPOINTS.livenessDetection(),
      {
        json: payload,
        ...fetcherSettings,
      },
    );
    return response.body;
  }
}

export {GuestApi};
export type {CreateGuestWithMinDataPayload};
